import { useCallback, useSyncExternalStore } from "react";

/**
 * Theme management — the single source of truth for light/dark.
 *
 * The user's *preference* is `system | light | dark` (persisted in
 * `localStorage['anyai-theme']`); the *resolved* theme is always `light | dark`.
 * The initial `.dark` class is set by the inline boot script in `index.html`
 * before React mounts (no flash); this hook keeps it in sync afterwards and
 * reacts to OS changes while the preference is `system`.
 *
 * Backed by a tiny module-level store consumed via `useSyncExternalStore`, so
 * the topbar toggle and the Settings → Appearance section share one state
 * without prop-drilling or a Context provider.
 */
export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "anyai-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

const listeners = new Set<() => void>();

function readStoredPreference(): ThemePreference {
  if (typeof localStorage === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" ? stored : "system";
}

let preference: ThemePreference = readStoredPreference();

function systemPrefersDark(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia(DARK_QUERY).matches;
}

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
}

function applyResolved(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

function emit(): void {
  for (const listener of listeners) listener();
}

function setPreference(next: ThemePreference): void {
  preference = next;
  if (typeof localStorage !== "undefined") {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  }
  applyResolved(resolve(next));
  emit();
}

// Keep the resolved theme honest when the OS flips while on `system`.
if (typeof matchMedia !== "undefined") {
  matchMedia(DARK_QUERY).addEventListener("change", () => {
    if (preference === "system") {
      applyResolved(resolve("system"));
      emit();
    }
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getPreference(): ThemePreference {
  return preference;
}

export interface UseThemeResult {
  /** The user's stored choice. */
  theme: ThemePreference;
  /** The theme actually applied right now (`system` collapsed to light/dark). */
  resolvedTheme: ResolvedTheme;
  setTheme: (preference: ThemePreference) => void;
  /** Flip between light and dark, setting an explicit preference. */
  toggleTheme: () => void;
}

export function useTheme(): UseThemeResult {
  const theme = useSyncExternalStore(subscribe, getPreference, getPreference);
  const resolvedTheme = resolve(theme);

  const setTheme = useCallback((next: ThemePreference) => setPreference(next), []);
  const toggleTheme = useCallback(
    () => setPreference(resolve(getPreference()) === "dark" ? "light" : "dark"),
    [],
  );

  return { theme, resolvedTheme, setTheme, toggleTheme };
}
